// ═══════════════════════════════════════════════════════════════════════════
// causaMargenNegativo — POR QUÉ el margen neto de una publicación es negativo
// ───────────────────────────────────────────────────────────────────────────
// Una regla, un sitio (molde ADR 078). Hasta el 09-sep-2026 este bloque estaba
// COPIADO byte a byte en `PublicidadDashboard.jsx` y en `clasificadores/
// publicidad.js`. Las dos copias alimentan "Pausar por rentabilidad"; la del
// clasificador, además, se empaqueta a `functions/agentesBundle/pipeline.cjs`
// (`scripts/build-agentes-bundle.mjs`) y su `motivo` termina PERSISTIDO en
// tareas creadas automáticamente en producción.
//
// EL TERCER CASO (report UQndSudKpBhxAxwjYoLE, 09-sep-2026)
// `functions/rentabilidad.js:265` descarta el anuncio entero cuando las
// unidades atribuidas son cero, aunque haya gastado: `cost <= 0 || units <= 0`.
// Con eso `publicidad = adUnit * qty` vale 0, y `margenResidual` y
// `margenResidualSinPub` (`functions/lib/rentabilidad.js:121` y `:133`) quedan
// IDÉNTICOS por construcción. Como los dos márgenes daban iguales, la pantalla
// sólo podía concluir "el producto pierde plata, revisar costo": le echaba la
// culpa al producto por un gasto de publicidad que nunca se contó, en la
// pantalla que se usa para decidir qué anuncio pausar.
//
// Acá no se inventa el número que falta: se deja de afirmar el que no se sabe.
// Prorratear ese gasto sobre las unidades vendidas cambia la rentabilidad de
// TODO el tablero y es decisión de Mariano — sigue abierta en el reporte.
// ═══════════════════════════════════════════════════════════════════════════

export const CAUSAS_MARGEN_NEGATIVO = Object.freeze({
  PUB_HUNDE: 'pub-hunde',
  PRODUCTO_PIERDE: 'producto-pierde',
  GASTO_SIN_ATRIBUIR: 'gasto-sin-atribuir',
})

const pct = (v) => `${(v * 100).toFixed(1)}%`

/**
 * ¿El margen "con publicidad" de esta publicación NO tiene gasto de publicidad
 * adentro, habiendo anuncios que gastaron?
 *
 * Se deriva de los DOS rollups persistidos de la misma publicación — la misma
 * fuente que produjo el número que se está explicando. No se re-deriva el
 * predicado `cost > 0 && units <= 0` sobre las métricas de ads vivas: ésas
 * pueden venir de otra ventana (`campanasMetricasVentanas`, selector de
 * 0/7/14/21/60/90 días en `usePublicidadData.js:142-175`) mientras el margen
 * sale siempre del rollup de 30d, y ahí el veredicto no sería comparable.
 *
 *   1. `margenPct === margenSinPubPct` bit a bit ⟺ el término de publicidad
 *      valía 0 en todas las líneas que entraron al promedio. `ponderar`
 *      (`functions/lib/rentabilidadRollups.js:29-41`) corre sobre los mismos
 *      pesos, así que la igualdad se preserva.
 *   2. `costoAdquisicionInferido.estado === 'sin_ventas'`, que se emite con
 *      `totalAmount === 0 && unidades === 0` (`functions/lib/costoAdquisicionRollups.js:108`).
 *      Es el que separa este caso de "esta publicación no tiene ads".
 *   3. `costoTotal > 0` — esa condición NO mira el gasto, así que un ad que no
 *      gastó nada también cae en `sin_ventas`. Sin este tercer chequeo se
 *      afirmaría "gastaron sin unidades atribuidas" sobre un anuncio que no gastó.
 *
 * COBERTURA PARCIAL, dicha acá porque es lo que el próximo va a leer: el guard
 * del margen mira SÓLO `units <= 0` (`functions/rentabilidad.js:265`), mientras
 * `sin_ventas` exige además `totalAmount === 0`. Un ad con unidades atribuidas
 * en 0 pero monto atribuido > 0 queda en `calculado`, y esa fila sigue diciendo
 * "el producto pierde". Se detecta el subconjunto seguro y nunca se afirma de
 * más; cerrarlo entero es parte del arreglo del motor, que sigue abierto.
 *
 * @param {{margenPctProm?: number|null, margenSinPubPctProm?: number|null}|null} rentInferida
 * @param {{estado?: string, costoTotal?: number}|null} costoAdquisicionInferido
 */
export function gastoSinAtribuirDePublicacion(rentInferida, costoAdquisicionInferido) {
  const conPub = rentInferida?.margenPctProm
  const sinPub = rentInferida?.margenSinPubPctProm
  if (conPub == null || sinPub == null) return false
  if (!Object.is(conPub, sinPub)) return false
  if (costoAdquisicionInferido?.estado !== 'sin_ventas') return false
  return Number(costoAdquisicionInferido?.costoTotal || 0) > 0
}

/**
 * @param {Object} args
 * @param {number} args.margenPct           margen CON publicidad (0..1), rollup Capa 2.
 * @param {number|null} args.margenSinPubPct margen SIN publicidad (0..1), mismo rollup.
 * @param {boolean} args.gastoSinAtribuir   de `gastoSinAtribuirDePublicacion`.
 * @returns {{causa: string, motivo: string}}
 */
export function causaMargenNegativo({ margenPct, margenSinPubPct, gastoSinAtribuir = false }) {
  if (gastoSinAtribuir) {
    // Ni se afirma un margen "con publicidad" que no incluye la publicidad, ni
    // se acusa al producto de una pérdida que puede ser del anuncio.
    return {
      causa: CAUSAS_MARGEN_NEGATIVO.GASTO_SIN_ATRIBUIR,
      motivo: `Pausar. Los anuncios de esta publicación gastaron sin unidades atribuidas, así que ese gasto NO entró en el margen: el margen con publicidad no se puede calcular, y es PEOR que el ${margenSinPubPct != null ? pct(margenSinPubPct) : '?'} de sin publicidad.`,
    }
  }

  const pubHunde = (margenSinPubPct != null && margenSinPubPct >= 0)
  return {
    causa: pubHunde ? CAUSAS_MARGEN_NEGATIVO.PUB_HUNDE : CAUSAS_MARGEN_NEGATIVO.PRODUCTO_PIERDE,
    motivo: pubHunde
      ? `Pausar. Margen con pub: ${pct(margenPct)} < 0. Sin publicidad sería ${pct(margenSinPubPct)} — la pub está hundiendo el margen.`
      : `Pausar. Margen con pub: ${pct(margenPct)} < 0. Sin pub también: ${margenSinPubPct != null ? pct(margenSinPubPct) : '?'} — el producto pierde plata, revisar costo.`,
  }
}
